import type { IndexStatus as Status } from '../api/types.js';
import { useFillIndex, useIndexStatus } from '../hooks/use-index.js';
import { SectionAction, SettingsSection } from './settings-section.js';

// The server's index (ADR-042), on its Settings tab (operator, 30/10/2026): what it holds, how its facets are spread,
// whether the sessions find something in it, and its filling with what existed before it, started by an admin.

const KIND: Record<string, [string, string]> = {
  subject: ['subject', 'subjects'], review: ['review', 'reviews'], 'jira-analysis': ['Jira analysis', 'Jira analyses'],
  plan: ['plan', 'plans'], mail: ['mail', 'mails'], slack: ['Slack conversation', 'Slack conversations'],
  meeting: ['meeting', 'meetings'], deployment: ['deployment', 'deployments'], 'session-note': ['session note', 'session notes'],
};

const counted = (n: number, kind: string) => `${n} ${(KIND[kind] ?? [kind, kind])[n === 1 ? 0 : 1]}`;

const when = (iso: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Paris' }).format(new Date(iso));

/** One line on what the sessions found: the index is worth what they get out of it */
function searchesLine(s: Status['searches']): string {
  if (!s.total) return `No session searched it in the last ${s.days} days.`;
  return `Sessions searched it ${s.total === 1 ? 'once' : `${s.total} times`} in the last ${s.days} days, ${s.found} with a result.`;
}

/** What the last filling added, and whether there is more to fill */
function backfillLine(b: NonNullable<Status['backfill']>): string {
  const added = [['subject', b.subjects], ['review', b.reviews], ['jira-analysis', b.tickets], ['meeting', b.meetings]] as const;
  const what = added.filter(([, n]) => n > 0).map(([k, n]) => counted(n, k));
  return [
    `Last filled ${when(b.at)}: ${what.length ? `${what.join(', ')} added` : 'nothing left to add'}`,
    b.failed ? `, ${b.failed} not described (tried again next time)` : '',
    b.stopped ? '. It stopped on an error: fill it again' : b.more ? '. There is more: fill it again' : '',
    '.',
  ].join('');
}

export function IndexStatus({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data, isLoading, error } = useIndexStatus(serverId);
  const fill = useFillIndex(serverId);
  const running = !!data?.running || fill.isPending;
  const action = isAdmin && data ? (
    <SectionAction onClick={() => fill.mutate()} disabled={running}>{running ? 'Filling…' : 'Fill the index'}</SectionAction>
  ) : undefined;
  if (error) return <SettingsSection id="index-title" title="Index" tone="error" state={`Not read: ${(error as Error).message}`} />;
  const total = data?.entries.total ?? 0;
  const kinds = Object.entries(data?.entries.byKind ?? {}).sort((a, b) => b[1] - a[1]);
  const values = (data?.facets ?? []).reduce((n, f) => n + f.values, 0);
  const other = data?.facets.find((f) => f.dimension === 'other')?.values ?? 0;
  return (
    <SettingsSection id="index-title" title="Index" loading={isLoading} action={action}
      tone={total ? 'normal' : 'muted'}
      state={!data ? undefined : total
        ? `${total} ${total === 1 ? 'entry' : 'entries'}${data.lastWrittenAt ? `, the last written ${when(data.lastWrittenAt)}` : ''}. ${searchesLine(data.searches)}`
        : `Nothing indexed yet.${isAdmin ? ' Fill it with the subjects, reviews and analyses kept so far.' : ''}`}>
      {data && total > 0 && (
        <p className="mt-2 max-w-[68ch] text-sm" style={{ color: 'var(--color-text-2)' }}>
          {kinds.map(([k, n]) => counted(n, k)).join(', ')}.
        </p>
      )}
      {data?.backfill && (
        <p className="mt-1 max-w-[68ch] text-sm" style={{ color: data.backfill.stopped ? 'var(--color-warn)' : 'var(--color-text-3)' }}>
          {running ? 'Being filled now.' : backfillLine(data.backfill)}
        </p>
      )}
      {fill.error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not started: {(fill.error as Error).message}</p>}
      {data && values > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Facets</h3>
          <p className="mt-0.5 max-w-[68ch] text-sm" style={{ color: other / values > 0.3 ? 'var(--color-warn)' : 'var(--color-text-3)' }}>
            {`${values} values in ${data.facets.length} ${data.facets.length === 1 ? 'dimension' : 'dimensions'}`}
            {other > 0 && `, ${other} of them in « other » (${Math.round((100 * other) / values)} %)`}
            {other / values > 0.3 ? ': the search finds less what has no dimension of its own.' : '.'}
          </p>
          <dl className="mt-2 grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1.5 text-sm">
            {data.facets.map((f) => (
              <div key={f.dimension} className="contents">
                <dt className="tabular-nums" style={{ color: 'var(--color-text-2)' }}>
                  {f.dimension} <span style={{ color: 'var(--color-text-3)' }}>{f.values}</span>
                </dt>
                <dd className="min-w-0 break-words" style={{ color: 'var(--color-text-3)' }}>
                  {f.top.map((v) => `${v.value} (${v.count})`).join(', ')}{f.values > f.top.length ? ', …' : ''}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </SettingsSection>
  );
}
